import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Warning12Filled,
    Dismiss12Regular,
    DismissCircle12Filled,
    Circle12Regular,
    ErrorCircle12Filled,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody } from '../../../utils/askLensJournal';
import { buildNavigatePath } from '../../../utils/responseActionHandlers';
import {
    filterSupportedInsightsActions,
    firstSupportedInsightsAction,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import { buildSourceReadinessReviewFocus } from '../../../utils/intelligenceSourceReadiness';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import styles from './intelligenceInsightsModules.module.css';
import drawerStyles from './intelligenceInsightsHighlights.module.css';

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function qualityFillPercent(value) {
    const v = String(value || '').toLowerCase();
    if (v === 'high' || v === 'strong') return 80;
    if (v === 'moderate' || v === 'medium') return 60;
    if (v === 'partial') return 40;
    if (v === 'low' || v === 'limited' || v === 'weak') return 30;
    return 50;
}

function coveragePillClass(coverageKey) {
    const key = String(coverageKey || '').toLowerCase();
    if (key === 'strong') return styles.coveragePillStrong;
    if (key === 'limited') return styles.coveragePillLimited;
    return styles.coveragePillPartial;
}

function evidenceCoverageBadgeClass(coverageKey) {
    const key = String(coverageKey || '').toLowerCase();
    if (key === 'strong') return styles.evidenceCoverageStrong;
    if (key === 'limited') return styles.evidenceCoverageLimited;
    return '';
}

function priorityPillClass(priorityKey) {
    const key = String(priorityKey || '').toLowerCase();
    if (key === 'critical') return styles.priorityCritical;
    if (key === 'high') return styles.priorityHigh;
    return styles.priorityMedium;
}

function typeChipClass(typeKey) {
    const key = String(typeKey || '').toLowerCase();
    if (key.includes('contradiction')) return styles.typeChipContradiction;
    if (key.includes('gap') || key.includes('evidence')) return styles.typeChipGap;
    return '';
}

function statusChipClass(statusKey) {
    const key = String(statusKey || '').toLowerCase();
    if (key === 'in_progress' || key === 'in progress') return styles.statusChipProgress;
    return styles.statusChip;
}

function isPriorityToken(value) {
    const v = String(value || '')
        .trim()
        .toLowerCase();
    return ['critical', 'high', 'medium', 'low', 'material', 'notable', 'severe'].includes(v);
}

function descriptiveText(...values) {
    return values.find(v => v && !isPriorityToken(v)) || '';
}

function followUpPrefill(item) {
    const detail = item?.detail || {};
    return [
        item?.title,
        detail.missingOrUnresolved || item?.body,
        detail.whyItMatters,
        detail.recommendedRequest,
    ]
        .filter(Boolean)
        .join('\n\n');
}

function formatSourceMix(sourceMix) {
    if (!sourceMix || typeof sourceMix !== 'object') return '';
    return Object.entries(sourceMix)
        .map(([label, count]) => `${label} ${count}`)
        .join(' · ');
}

function workflowStatusBadgeClass(statusKey) {
    const key = String(statusKey || '').toLowerCase().replace(/\s+/g, '_');
    if (key === 'in_progress' || key === 'in progress') {
        return styles.efuWorkflowStatusBadge;
    }
    return styles.efuWorkflowOpenBadge;
}

function AreaDetailDrawer({ open, area, onClose, onAction }) {
    if (!open || !area) return null;
    const detail = area.detail || {};
    const quality = detail.evidenceQuality || [];
    const sourceMixText = formatSourceMix(detail.sourceMix);
    const openMatters = detail.openMatters || [];

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <h2 className={styles.swotDetailTitle}>{area.label}</h2>
                            <span
                                className={`${styles.efuDetailHeaderBadge} ${styles.efuDetailHeaderBadgeNeutral}`}>
                                {area.coverage}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={drawerStyles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={`${styles.swotDetailBody} ${styles.efuDetailBody}`}>
                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Coverage</p>
                            <div className={styles.efuCoverageRow}>
                                <span
                                    className={`${styles.coveragePill} ${coveragePillClass(
                                        area.coverageKey,
                                    )}`}>
                                    {area.coverage}
                                </span>
                                <span className={styles.efuCoverageConfidence}>
                                    Confidence: {area.confidence}
                                </span>
                            </div>
                        </section>

                        {detail.wellSupported?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Well-supported topics</p>
                                <div className={styles.efuTopicList}>
                                    {detail.wellSupported.map(topic => (
                                        <div key={topic} className={styles.efuTopicItem}>
                                            <span
                                                className={`${styles.topicDot} ${styles.topicDotGreen}`}
                                                aria-hidden
                                            />
                                            {topic}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {detail.weaklySupported?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Weakly supported topics</p>
                                <div className={styles.efuTopicList}>
                                    {detail.weaklySupported.map(topic => (
                                        <div key={topic} className={styles.efuTopicItem}>
                                            <span
                                                className={`${styles.topicDot} ${styles.topicDotAmber}`}
                                                aria-hidden
                                            />
                                            {topic}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {detail.assessmentImpact ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Assessment impact</p>
                                <p className={styles.efuAssessmentImpactText}>
                                    {detail.assessmentImpact}
                                </p>
                            </section>
                        ) : null}

                        {quality.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Evidence quality</p>
                                <div className={styles.qualityBars}>
                                    {quality.map(row => (
                                        <div key={row.label} className={styles.qualityRow}>
                                            <p className={styles.qualityLabel}>{row.label}</p>
                                            <div className={styles.qualityTrack}>
                                                <div
                                                    className={styles.qualityFill}
                                                    style={{
                                                        width: `${qualityFillPercent(row.value)}%`,
                                                    }}
                                                />
                                            </div>
                                            <p className={styles.qualityValue}>{row.value}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {sourceMixText ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Source mix</p>
                                <p className={styles.efuSourceMixText}>{sourceMixText}</p>
                            </section>
                        ) : null}

                        {openMatters.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Open evidence matters</p>
                                {openMatters.map(item => (
                                    <button
                                        key={item}
                                        type="button"
                                        className={styles.efuOpenMatterLink}
                                        onClick={() =>
                                            onAction?.(
                                                { key: 'view_details', label: 'View details' },
                                                { title: item, body: item },
                                            )
                                        }>
                                        {item}
                                    </button>
                                ))}
                            </section>
                        ) : null}

                        <button
                            type="button"
                            className={styles.efuViewEvidenceLink}
                            onClick={() =>
                                onAction?.(
                                    { key: 'view_evidence', label: 'View evidence' },
                                    area,
                                )
                            }>
                            View evidence →
                        </button>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

function followUpTypeLabel(item) {
    const raw = item?.type || '';
    if (raw && !String(raw).includes('_')) return raw;
    const key = String(item?.typeKey || 'evidence_gap').toLowerCase();
    return titleCase(key.replace(/_/g, ' '));
}

function FollowUpDetailDrawer({ open, item, onClose, onAction }) {
    if (!open || !item) return null;
    const detail = item.detail || {};
    const missingText = descriptiveText(
        detail.missingOrUnresolved,
        item.body,
        item.title,
    );
    const whyCandidate = descriptiveText(detail.whyItMatters);
    const whyText =
        whyCandidate &&
        whyCandidate.toLowerCase() !== String(missingText || '').toLowerCase()
            ? whyCandidate
            : '';
    const affected =
        detail.affectedAssessments?.length > 0
            ? detail.affectedAssessments
            : item.scopes || [];
    const destinationLabel = detail.recommendedDestination || 'Create IRL';
    const destinationKey =
        String(destinationLabel).toLowerCase().includes('raid') ? 'add_to_raid' : 'create_irl';
    const typeLabel = followUpTypeLabel(item);
    const workflowLabel = item.workflow && item.workflow !== '—' ? item.workflow : 'Open';
    const workflowKey =
        item.workflowKey && item.workflowKey !== '—' ? item.workflowKey : 'open';
    const recommendedRequest =
        detail.recommendedRequest ||
        (item.title
            ? `Provide documentation or evidence to resolve: ${item.title}.`
            : '');

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.swotDetailRoot}>
                    <div className={styles.swotDetailHeader}>
                        <div className={styles.swotDetailHeaderText}>
                            <h2 className={styles.swotDetailTitle}>{item.title}</h2>
                            <span className={styles.efuDetailHeaderBadge}>
                                {item.typeKey?.includes('contradiction') ? (
                                    <ErrorCircle12Filled aria-hidden />
                                ) : (
                                    <Circle12Regular aria-hidden />
                                )}
                                {typeLabel}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={drawerStyles.drawerClose}
                            onClick={onClose}
                            aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={`${styles.swotDetailBody} ${styles.efuDetailBody}`}>
                        {missingText ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Missing or unresolved</p>
                                <p className={styles.swotDetailLead}>{missingText}</p>
                            </section>
                        ) : null}

                        {whyText ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Why it matters</p>
                                <p className={styles.efuAssessmentImpactText}>{whyText}</p>
                            </section>
                        ) : null}

                        {affected.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Affected assessments</p>
                                <div className={styles.chipRow} style={{ paddingTop: 6 }}>
                                    {affected.map(label => (
                                        <span key={label} className={styles.objectChip}>
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {detail.ragImpact ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>RAG impact</p>
                                <p className={styles.efuImpactAmber}>
                                    <Warning12Filled aria-hidden />
                                    {detail.ragImpact}
                                </p>
                            </section>
                        ) : null}

                        {detail.confidenceImpact ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Confidence impact</p>
                                <p className={styles.efuImpactMuted}>{detail.confidenceImpact}</p>
                            </section>
                        ) : null}

                        {detail.coverageImpact ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Coverage impact</p>
                                <p className={styles.efuImpactMuted}>{detail.coverageImpact}</p>
                            </section>
                        ) : null}

                        {detail.recommendedRequest || recommendedRequest ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Recommended request</p>
                                <div className={styles.efuRecommendedRequest}>
                                    {detail.recommendedRequest || recommendedRequest}
                                </div>
                            </section>
                        ) : null}

                        {destinationLabel ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Recommended destination</p>
                                <button
                                    type="button"
                                    className={styles.efuDestinationBtn}
                                    onClick={() =>
                                        onAction?.(
                                            { key: destinationKey, label: destinationLabel },
                                            item,
                                        )
                                    }>
                                    {destinationLabel}
                                </button>
                            </section>
                        ) : null}

                        {detail.existingWorkflowRecords?.length ? (
                            <section className={styles.swotDetailSection}>
                                <p className={styles.swotDetailLabel}>Existing workflow records</p>
                                <div className={styles.efuTopicList}>
                                    {detail.existingWorkflowRecords.map(rec => (
                                        <div
                                            key={rec.label || rec.status}
                                            className={styles.efuWorkflowRow}>
                                            <span className={styles.efuWorkflowLabel}>
                                                {rec.label}
                                            </span>
                                            {rec.status ? (
                                                <span
                                                    className={workflowStatusBadgeClass(
                                                        rec.status,
                                                    )}>
                                                    {rec.status}
                                                </span>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Workflow status</p>
                            <div style={{ paddingTop: 6 }}>
                                <span className={workflowStatusBadgeClass(workflowKey)}>
                                    {workflowLabel}
                                </span>
                            </div>
                        </section>

                        <section className={styles.swotDetailSection}>
                            <p className={styles.swotDetailLabel}>Actions</p>
                            <div className={styles.efuActionRow}>
                                <button
                                    type="button"
                                    className={styles.efuActionBtn}
                                    onClick={() =>
                                        onAction?.(
                                            { key: 'create_irl', label: 'Create IRL' },
                                            item,
                                        )
                                    }>
                                    Create IRL
                                </button>
                                <button
                                    type="button"
                                    className={styles.efuActionBtn}
                                    onClick={() =>
                                        onAction?.(
                                            { key: 'add_to_raid', label: 'Add to RAID' },
                                            item,
                                        )
                                    }>
                                    Add to RAID
                                </button>
                            </div>
                        </section>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

const IntelligenceInsightsEvidenceFollowUps = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onReviewFailedSource,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    initialEvidenceMatterId = null,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const readOnly = useInsightsReadOnly();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const notesState = useSelector(state => state?.dashboard?.notes);

    const filterDefaults = data?.filters || {};
    const [priorityFilter, setPriorityFilter] = useState(filterDefaults.priority || 'all');
    const [scopeFilter, setScopeFilter] = useState(filterDefaults.scope || 'all');
    const [typeFilter, setTypeFilter] = useState(filterDefaults.type || 'all');
    const [impactFilter, setImpactFilter] = useState(filterDefaults.impact || 'all');
    const [statusFilter, setStatusFilter] = useState(filterDefaults.status || 'all');
    const [unresolvedOnly, setUnresolvedOnly] = useState(
        filterDefaults.unresolvedOnly !== false,
    );
    const [detailArea, setDetailArea] = useState(null);
    const [detailFollowUp, setDetailFollowUp] = useState(null);
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showIrlSidebar, setShowIrlSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);
    const [showObsSidebar, setShowObsSidebar] = useState(false);

    const closeOverlays = () => {
        setDetailArea(null);
        setDetailFollowUp(null);
        setEvidenceTarget(null);
    };

    useEffect(() => {
        if (!initialEvidenceMatterId || !data?.followUps?.length) return;
        const match = data.followUps.find(
            f =>
                f.id === initialEvidenceMatterId ||
                f.id === String(initialEvidenceMatterId),
        );
        if (match) {
            setDetailFollowUp(match);
        }
    }, [initialEvidenceMatterId, data?.followUps]);

    const handleAction = async (action, context = null) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key || action.actionType || action.action_type;
        const target = action.target || {};
        const isArea = context && (context.key || context.coverageKey) && !context.typeKey;
        const text = isArea
            ? [context.label, context.primaryLimitation, context.detail?.assessmentImpact]
                  .filter(Boolean)
                  .join('\n\n')
            : followUpPrefill(context) || data?.overallPosition?.summary || '';

        if (key === 'view_details') {
            setEvidenceTarget(null);
            if (isArea) setDetailArea(context);
            else setDetailFollowUp(context);
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setDetailArea(null);
            setDetailFollowUp(null);
            const objectType = target.object_type || target.type || null;
            const objectId = target.object_id || target.id || null;
            if (objectType && objectId) {
                setEvidenceTarget({
                    mode: 'object',
                    objectType,
                    objectId,
                    area: context?.key || context?.scopeKeys?.[0] || null,
                });
                return;
            }
            setEvidenceTarget({
                mode: 'scope',
                area: context?.key || context?.scopeKeys?.[0] || 'technology_architecture',
            });
            return;
        }
        if (key === 'create_irl') {
            setSidebarContent(text);
            closeOverlays();
            setShowIrlSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(text);
            closeOverlays();
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'open_raid_item' || key === 'open_raid') {
            closeOverlays();
            const path = buildNavigatePath(
                {
                    route: target.route || action.route || '/raid',
                    target: {
                        type: 'raid',
                        id: target.raid_item_id || target.id,
                    },
                },
                projectId,
            );
            navigate(path || '/raid');
            return;
        }
        if (key === 'create_observation') {
            setSidebarContent(text);
            closeOverlays();
            setShowObsSidebar(true);
            return;
        }
        if (key === 'review_sources' || key === 'review_failed') {
            onReviewFailedSource?.(
                buildSourceReadinessReviewFocus(data?.sourceReadiness || {}),
            );
            return;
        }
        if (key === 'generate_analysis' || key === 'generate_new_version') {
            onGenerateAnalysis?.();
            return;
        }
        if (key === 'save_to_journal') {
            const journalSource =
                context?.item || (context?.detail || context?.body || context?.title ? context : null);
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Evidence & Follow-Ups',
                    headline:
                        context?.title ||
                        journalSource?.title ||
                        data?.overallPosition?.headline ||
                        'Evidence position',
                    bodyText:
                        buildInsightsItemJournalBody(journalSource) ||
                        context?.body ||
                        context?.text ||
                        text ||
                        data?.overallPosition?.summary ||
                        '',
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
    };

    if (error || !data || data.isEmpty) {
        const failed =
            Boolean(error) ||
            data?.status === 'failed' ||
            data?.emptyState?.type === 'failed';
        return (
            <InsightsModuleChrome
                title={data?.title || 'Evidence & Follow-Ups'}
                subtitle={
                    data?.subtitle ||
                    'Evidence coverage, reliability and required follow-up actions.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="evidence_follow_ups"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    variant={failed ? 'failed' : 'not_generated'}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    const position = data.overallPosition || {};
    const metrics = position.metrics || {};
    const matrix = data.coverageMatrix || [];
    const followUps = data.followUps || data.matters || [];
    const filterOptions = data.filters?.options || {};
    const scopeLabels = filterOptions.scopeAreaLabels || {};
    const scopeOptions =
        filterOptions.scope?.length > 1
            ? filterOptions.scope
            : [
                  'all',
                  ...Array.from(new Set(followUps.flatMap(f => f.scopeKeys || []))).sort(),
              ];
    const impactOptions =
        filterOptions.impact?.length > 1
            ? filterOptions.impact
            : ['all', ...Array.from(new Set(followUps.map(f => f.impact).filter(Boolean))).sort()];

    const filteredFollowUps = followUps.filter(item => {
        if (
            priorityFilter !== 'all' &&
            String(item.priorityKey || item.priority || '').toLowerCase() !==
                priorityFilter.toLowerCase()
        ) {
            return false;
        }
        if (scopeFilter !== 'all') {
            const keys = item.scopeKeys || [];
            if (
                !keys.some(s => String(s).toLowerCase() === scopeFilter.toLowerCase())
            ) {
                return false;
            }
        }
        if (typeFilter !== 'all') {
            const typeKey = String(item.typeKey || item.type || '')
                .toLowerCase()
                .replace(/\s+/g, '_');
            if (!typeKey.includes(typeFilter.toLowerCase().replace(/\s+/g, '_'))) {
                return false;
            }
        }
        if (
            impactFilter !== 'all' &&
            String(item.impact || '').toLowerCase() !== impactFilter.toLowerCase()
        ) {
            return false;
        }
        if (statusFilter !== 'all') {
            const statusKey = String(item.workflowKey || item.status || '')
                .toLowerCase()
                .replace(/\s+/g, '_');
            if (statusKey !== statusFilter.toLowerCase().replace(/\s+/g, '_')) {
                return false;
            }
        }
        if (unresolvedOnly) {
            const key = String(item.workflowKey || item.status || '')
                .toLowerCase()
                .replace(/\s+/g, '_');
            if (key === 'resolved' || key === 'closed' || key === 'mitigated') {
                return false;
            }
        }
        return true;
    });

    const positionActions = position.actions?.length
        ? position.actions
        : [{ key: 'save_to_journal', label: 'Save to Journal' }];
    const sourceReadiness = data.sourceReadiness || {};
    const failedSourceLabel =
        sourceReadiness.failedSourceLabel ||
        (sourceReadiness.unavailableSources || []).find(
            s => String(s?.reason || '').toLowerCase() === 'failed',
        )?.label ||
        null;

    return (
        <>
            <InsightsModuleChrome
                title={data.title}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={onAskAi}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}
                demoBadge={false}>
                <section className={styles.evidencePositionCard}>
                    <div className={styles.evidencePositionLeft}>
                        <span
                            className={`${styles.evidenceCoverageBadge} ${evidenceCoverageBadgeClass(
                                position.coverageKey,
                            )}`}>
                            {position.coverage || 'Partial'}
                        </span>
                        {typeof position.summary === 'string' && position.summary ? (
                            <p className={styles.evidencePositionSummary}>{position.summary}</p>
                        ) : null}
                        {position.limitedAreas?.length ? (
                            <p className={styles.evidenceLimitedNote}>
                                <Warning12Filled aria-hidden />
                                Limited in {position.limitedAreas.join(', ')}
                            </p>
                        ) : null}
                        <div className={styles.actionRow}>
                            {positionActions.map(action => (
                                <button
                                    key={action.key}
                                    type="button"
                                    className={styles.btnOutline}
                                    onClick={() =>
                                        handleAction(action, {
                                            title: 'Overall evidence position',
                                            body: position.summary,
                                        })
                                    }>
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className={styles.evidenceMetrics}>
                        <div className={styles.evidenceMetric}>
                            <p className={styles.evidenceMetricValue}>
                                {metrics.readySources ?? 0}
                            </p>
                            <p className={styles.evidenceMetricLabel}>Ready sources</p>
                        </div>
                        <div className={styles.evidenceMetric}>
                            <p className={styles.evidenceMetricValue}>
                                {metrics.coverage || position.coverage || '—'}
                            </p>
                            <p className={styles.evidenceMetricLabel}>Coverage</p>
                        </div>
                        <div className={styles.evidenceMetric}>
                            <p className={styles.evidenceMetricValue}>
                                {metrics.openMatters ?? 0}
                            </p>
                            <p className={styles.evidenceMetricLabel}>Open matters</p>
                        </div>
                    </div>
                </section>

                <section>
                    <p className={styles.sectionTitle}>Coverage matrix</p>
                    <div className={styles.coverageTable}>
                        <div className={styles.coverageHead}>
                            <span>Area</span>
                            <span>Coverage</span>
                            <span>Confidence</span>
                            <span>Open</span>
                            <span>Primary limitation</span>
                        </div>
                        {matrix.length ? (
                            matrix.map(row => (
                                <div
                                    key={row.id || row.key}
                                    role="button"
                                    tabIndex={0}
                                    className={`${styles.coverageRow} ${
                                        detailArea?.id === row.id ? styles.coverageRowActive : ''
                                    }`}
                                    onClick={() => setDetailArea(row)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setDetailArea(row);
                                        }
                                    }}>
                                    <p className={styles.coverageArea}>{row.label}</p>
                                    <span
                                        className={`${styles.coveragePill} ${coveragePillClass(
                                            row.coverageKey,
                                        )}`}>
                                        {row.coverage}
                                    </span>
                                    <span className={styles.confidenceCell}>{row.confidence}</span>
                                    <span
                                        className={`${styles.openCountPill} ${
                                            row.openMatters > 0 ? styles.openCountPillWarn : ''
                                        }`}>
                                        {row.openMatters}
                                    </span>
                                    <p className={styles.limitationCell}>
                                        {row.primaryLimitation || '—'}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <p className={styles.emptyNote} style={{ padding: 16 }}>
                                No coverage rows available.
                            </p>
                        )}
                    </div>
                </section>

                <section>
                    <div className={styles.sectionTitleRow}>
                        <p className={styles.sectionTitle}>Priority follow-ups</p>
                        <span className={styles.sectionCountBadge}>{filteredFollowUps.length}</span>
                    </div>
                    <div className={styles.filters} style={{ marginBottom: 12 }}>
                        <div className={styles.filterGroup}>
                            <span className={styles.filterLabel}>Priority:</span>
                            <select
                                className={styles.filterSelect}
                                value={priorityFilter}
                                onChange={e => setPriorityFilter(e.target.value)}
                                aria-label="Priority filter">
                                {(
                                    filterOptions.priority || [
                                        'all',
                                        'critical',
                                        'high',
                                        'medium',
                                        'low',
                                    ]
                                ).map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : titleCase(opt)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <span className={styles.filterLabel}>Scope:</span>
                            <select
                                className={styles.filterSelect}
                                value={scopeFilter}
                                onChange={e => setScopeFilter(e.target.value)}
                                aria-label="Scope filter">
                                {scopeOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {scopeLabels[opt] ||
                                            (opt === 'all' ? 'All' : titleCase(opt))}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <span className={styles.filterLabel}>Type:</span>
                            <select
                                className={styles.filterSelect}
                                value={typeFilter}
                                onChange={e => setTypeFilter(e.target.value)}
                                aria-label="Type filter">
                                {(
                                    filterOptions.type || [
                                        'all',
                                        'evidence_gap',
                                        'contradiction',
                                    ]
                                ).map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : titleCase(opt)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <span className={styles.filterLabel}>Impact:</span>
                            <select
                                className={styles.filterSelect}
                                value={impactFilter}
                                onChange={e => setImpactFilter(e.target.value)}
                                aria-label="Impact filter">
                                {impactOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.filterGroup}>
                            <span className={styles.filterLabel}>Status:</span>
                            <select
                                className={styles.filterSelect}
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                                aria-label="Status filter">
                                {(
                                    filterOptions.status || [
                                        'all',
                                        'open',
                                        'in_progress',
                                        'resolved',
                                    ]
                                ).map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : titleCase(opt)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <label className={styles.filterCheckbox}>
                            <input
                                type="checkbox"
                                checked={unresolvedOnly}
                                onChange={e => setUnresolvedOnly(e.target.checked)}
                            />
                            Unresolved only
                        </label>
                    </div>

                    <div className={styles.followUpTable}>
                        <div className={styles.followUpHead}>
                            <span>Follow-up</span>
                            <span>Type</span>
                            <span>Scope</span>
                            <span>Priority</span>
                            <span>Impact</span>
                            <span>Workflow</span>
                            <span>Action</span>
                        </div>
                        {filteredFollowUps.length ? (
                            filteredFollowUps.map(item => {
                            const action = firstSupportedInsightsAction(
                                [
                                    item.action,
                                    ...(item.actions || []),
                                    { key: 'create_irl', label: 'Create IRL' },
                                ],
                                {
                                    moduleKey: 'evidence_follow_ups',
                                    context: { item },
                                    handlers: { onReviewFailedSource, onGenerateAnalysis },
                                },
                            );
                                return (
                                    <div
                                        key={item.id}
                                        role="button"
                                        tabIndex={0}
                                        className={`${styles.followUpRow} ${
                                            detailFollowUp?.id === item.id
                                                ? styles.followUpRowActive
                                                : ''
                                        }`}
                                        onClick={() => setDetailFollowUp(item)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setDetailFollowUp(item);
                                            }
                                        }}>
                                        <p className={styles.followUpTitle}>{item.title}</p>
                                        <span
                                            className={`${styles.typeChip} ${typeChipClass(
                                                item.typeKey,
                                            )}`}>
                                            {item.type}
                                        </span>
                                        <div>
                                            {(item.scopes || []).map(scope => (
                                                <span key={scope} className={styles.scopeChip}>
                                                    {scope}
                                                </span>
                                            ))}
                                        </div>
                                        <span
                                            className={`${styles.priorityPill} ${priorityPillClass(
                                                item.priorityKey,
                                            )}`}>
                                            {item.priority}
                                        </span>
                                        <span className={styles.confidenceCell}>
                                            {item.impact || '—'}
                                        </span>
                                        <span className={statusChipClass(item.workflowKey)}>
                                            {item.workflow}
                                        </span>
                                        <span>
                                            {action ? (
                                                <button
                                                    type="button"
                                                    className={styles.btnOutline}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        handleAction(action, item);
                                                    }}>
                                                    {action.label}
                                                </button>
                                            ) : null}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <p className={styles.emptyNote} style={{ padding: 16 }}>
                                No follow-ups match the current filters.
                            </p>
                        )}
                    </div>
                </section>

                {(sourceReadiness.ready ||
                    sourceReadiness.processing ||
                    sourceReadiness.failed) && (
                    <section className={styles.evidenceReadinessBar}>
                        <div className={styles.evidenceReadinessTotals}>
                            <span className={styles.evidenceReadinessItem}>
                                <span className={styles.readyDot} aria-hidden />
                                {sourceReadiness.ready ?? 0} Ready
                            </span>
                            <span className={styles.evidenceReadinessSep}>·</span>
                            <span className={styles.evidenceReadinessItem}>
                                <span className={styles.processingDot} aria-hidden />
                                {sourceReadiness.processing ?? 0} Processing
                            </span>
                            <span className={styles.evidenceReadinessSep}>·</span>
                            <span className={styles.evidenceReadinessItem}>
                                <DismissCircle12Filled
                                    className={styles.failedIcon}
                                    aria-hidden
                                />
                                {sourceReadiness.failed ?? 0} Failed
                            </span>
                        </div>
                        {failedSourceLabel && onReviewFailedSource ? (
                            <div className={styles.evidenceReadinessFailed}>
                                <span className={styles.evidenceReadinessFailedLabel}>
                                    {failedSourceLabel}
                                </span>
                                <button
                                    type="button"
                                    className={styles.btnOutline}
                                    onClick={() =>
                                        handleAction({ key: 'review_failed', label: 'Review' })
                                    }>
                                    Review
                                </button>
                            </div>
                        ) : null}
                    </section>
                )}
            </InsightsModuleChrome>

            <AreaDetailDrawer
                open={Boolean(detailArea)}
                area={detailArea}
                onClose={() => setDetailArea(null)}
                onAction={handleAction}
            />
            <FollowUpDetailDrawer
                open={Boolean(detailFollowUp)}
                item={detailFollowUp}
                onClose={() => setDetailFollowUp(null)}
                onAction={handleAction}
            />

            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area || null}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
                onAction={(action, panelCard) => {
                    void handleAction(action, {
                        title: panelCard?.headline || 'Evidence',
                        body: panelCard?.conclusion || position.summary,
                    });
                }}
            />

            <PostInformationRequestSidebar
                show={showIrlSidebar}
                setShow={show => {
                    setShowIrlSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultRequest={() => sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultObservation={sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsEvidenceFollowUps;
